/**
 * Where a venue's events come from, in Doug's language rather than schema
 * words — and derived at read time so it can never go stale.
 *
 * `venues` carries no sourcing column and nothing writes one. A stored
 * sentence is a second copy that is stale the moment a recipe is re-seeded,
 * paused or fails, and keeping it fresh would need a job — and that job is
 * the thing that breaks (CLAUDE.md, "the bug this codebase keeps writing").
 * Every fact below already exists in `recipes` and `harvest_runs`, reached
 * through `venue_source_recipes` and `recipe_coverage`
 * (`supabase/migrations/20260921240000_venue_admin.sql`), so the sentence is
 * a pure function of them and there is nothing to keep in sync.
 *
 * Three honest cases, and no fourth one invented to smooth over a row that
 * does not fit:
 *
 *   1. `isOwn`      recipes.place_id is this venue, directly or via its
 *                    building — a connector built for this one place.
 *   2. aggregator    the recipe produces rows here but covers others too —
 *                    INDEX, cinematic-berlin. Say roughly how many.
 *   3. nothing       no recipe has ever produced a row here. Say so plainly.
 *
 * A paused connector is worded as a decision, never as a failure — getting
 * that wrong once already produced a note about the adapter count
 * contradicting itself (NEXT.md, 21 September).
 *
 * No imports, so `npm test` can load it directly: the same reason
 * `attribution.ts` and `reach.ts` have none.
 */

export type RecipeStatus = 'ok' | 'degraded' | 'broken' | 'blocked' | 'paused'

export interface SourceRecipe {
  id: string
  /** Already written in prose — "Berlin cinemas, via cinematic-berlin" — so
   *  it drops into a sentence with no translation. */
  name: string
  /** True when `recipes.place_id` resolves to this venue, directly or via
   *  its building: a connector built for this one place. */
  isOwn: boolean
  status: RecipeStatus
  /** `recipes.last_ok_at`. Null means it has never once worked. */
  lastOkAt: string | null
  /** `recipes.refresh_hours`. Null for a recipe seeded before the column
   *  had a value, which reads as "no schedule is known" rather than "never". */
  refreshHours: number | null
  /** Distinct places this recipe has ever produced a row for — the
   *  aggregator's reach, from `recipe_coverage`. Null when the count itself
   *  is unavailable rather than genuinely small; a real 1 still prints. */
  coversPlaces: number | null
  /** The most recent `harvest_runs` row for this recipe, or null when it
   *  has not run inside the window the caller asked about — matches
   *  HealthStrip's "no run in the last week" rather than guessing at zero. */
  lastRun: { status: string; rowsValid: number | null; startedAt: string } | null
  /** Rows this recipe has produced at or for this venue specifically —
   *  `venue_source_recipes.rows_here`. Used only to pick a primary source
   *  when more than one recipe touches a venue and none is `isOwn`. */
  rowsHere: number
}

function ago(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 36) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/** How often it is checked, in the phrase a person would use rather than a
 *  raw hour count — "every night" reads faster than "every 24 hours" and
 *  says the same thing. Falls back to the number for anything that does not
 *  divide evenly, rather than rounding and quietly lying about the cadence. */
function every(hours: number | null): string | null {
  if (!hours || hours <= 0) return null
  if (hours === 24) return 'every night'
  if (hours === 1) return 'every hour'
  if (hours % (24 * 7) === 0) {
    const weeks = hours / (24 * 7)
    return `every ${weeks === 1 ? '' : `${weeks} `}week${weeks === 1 ? '' : 's'}`
  }
  if (hours % 24 === 0) {
    const days = hours / 24
    return `every ${days} days`
  }
  return `every ${hours} hours`
}

function foundText(run: SourceRecipe['lastRun']): string | null {
  if (!run) return null
  const n = run.rowsValid ?? 0
  return `found ${n} event${n === 1 ? '' : 's'}`
}

/** The last-worked clause, shared by both cases: when it last worked and,
 *  where a run is on hand, what that run returned. */
function lastWorked(r: SourceRecipe): string {
  const found = foundText(r.lastRun)
  return found ? `It last worked ${ago(r.lastOkAt)}, and ${found}.` : `It last worked ${ago(r.lastOkAt)}.`
}

function ownSentence(r: SourceRecipe): string {
  if (r.status === 'paused') {
    // A decision, not a failure — worded that way on purpose.
    return `This venue has its own connector reading its programme page. ` +
      `Paused on purpose, so nothing new is arriving. It last worked ${ago(r.lastOkAt)}.`
  }
  if (r.status === 'ok') {
    const checked = every(r.refreshHours)
    return `This venue has its own connector reading its programme page. ` +
      `${checked ? `Checked ${checked}. ` : ''}${lastWorked(r)}`
  }
  // degraded, broken, blocked — a real failure, said as one.
  return `This venue has its own connector reading its programme page, but it is currently ${r.status}. ` +
    `It last worked ${ago(r.lastOkAt)}.`
}

function aggregatorSentence(r: SourceRecipe): string {
  const reach = r.coversPlaces != null && r.coversPlaces > 1
    ? `, which covers about ${r.coversPlaces} places rather than this one directly`
    : ''
  if (r.status === 'paused') {
    return `Its events come from ${r.name}${reach}. ` +
      `That connector is paused on purpose, so nothing new is arriving.`
  }
  if (r.status === 'ok') {
    const checked = every(r.refreshHours)
    return `Its events come from ${r.name}${reach}. ${checked ? `Checked ${checked}. ` : ''}${lastWorked(r)}`
  }
  return `Its events come from ${r.name}${reach}, which is currently ${r.status}. It last worked ${ago(r.lastOkAt)}.`
}

/**
 * The one recipe worth describing, when more than one has touched a venue.
 *
 * A connector built for this place outranks one that merely happens to
 * produce rows here, whatever the row counts say — `isOwn` wins outright.
 * Failing that, the recipe with the most rows here is the one Doug would
 * actually notice if it stopped.
 */
export function primarySource(recipes: SourceRecipe[]): SourceRecipe | null {
  if (!recipes.length) return null
  const own = recipes.find(r => r.isOwn)
  if (own) return own
  return [...recipes].sort((a, b) => b.rowsHere - a.rowsHere)[0]
}

/**
 * The sentence itself. Empty input is the third case, plainly: nothing
 * harvests this venue, and no fourth case is invented to soften that.
 */
export function sourcingSentence(recipes: SourceRecipe[]): string {
  const primary = primarySource(recipes)
  if (!primary) return 'Nothing harvests this venue. No connector supplies its events.'
  return primary.isOwn ? ownSentence(primary) : aggregatorSentence(primary)
}
